import React, {useState, useEffect} from "react";
import {useDataWithRoute, useTitleFromComponent} from "../../../../utils/hooks";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import {Card, Col, Container, Row} from "reactstrap";
import {VsiBubble, VsiH4} from "../../../../styled/App.styled";
import {genericColors} from "../../../../utils/chartUtils";
import Highcharts from "highcharts/highstock";
import ActivityGauge from "../../../../components/ActivityGauge";
import Grid from "../../../../components/Grid";
import styles from "../../../../utils/themeStyles";
import { useSearchParams } from "react-router-dom";
import VerticalBarChart from "../../../../components/VerticalBarChart";
import { dateFormatter } from "../../../../utils/gridFormatters";

const colDefs = [
    {
        'field': "pod_name",
        'headerName': "POD"
    },
    {
        'field': "collection_date",
        'headerName': "Collection Date",
        'cellRenderer': dateFormatter
    },
    {
        'field': "containers_count",
        'headerName': "Active Containers"
    },
    {
        'field': "pod_memory_pct",
        'headerName': "Memory Util"
    },
    {
        'field': "pod_cpu_pct",
        'headerName': "CPU Util"
    },
    {
        "field": "pod_storage_pct",
        "headerName": "Disk Util"
    }
];

const sortingArr = ['Build Errors', 'Compose Errors', 'Run Errors'];

export default function KubernetesNodeStatus({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams] = useSearchParams();
    const basicTextStyle = {
        color: styles[theme].textColor,
        fontFamily: styles[theme].fontFamily,
    };
    const [chartConfig, setChartConfig] = useState({});
    const [allFilCats, setAllFilCats] = useState([]);
    const [graphData, setGraphData] = useState({
        categories: [],
        series: []
    });

    const [data] = useDataWithRoute(`/api/v6/report/kubernetes_node_status_proc?client_id=${client.clientId}&period_id=${periodId}&node_name=${encodeURIComponent(searchParams.get("node_name"))}`);

    useEffect(() => {
        const validData = data?.recordset || [];
        if(validData.length) {
            barChartBuilder(validData)
        }
    }, [data]);
    
    useEffect(() => {
        const validData = data?.recordset || [];
        if(validData.length) {
            gaugeBuilder(validData || [])
        }
    }, [data]);

    const gaugeBuilder = (data) => {
        let dataDrill = data;
        const grouping = {
            containers_run: 0,
            containers_not_run: 0,
            containers_pause: 0,
            containers_stop: 0,
            containers_stop_with_err: 0,
            containers_stop_without_err: 0
        };

        dataDrill.forEach(datum => {
            Object.keys(datum).forEach(key => {
                if (typeof datum[key] === "number" && !key.endsWith("_id") && datum[key]) {
                    grouping[key] = (grouping[key] ?? 0) + datum[key];
                };
            });
        });

        const newData = {
            running: grouping.containers_run,
            not_running: grouping.containers_not_run,
            paused: grouping.containers_pause,
            stopped: grouping.containers_stop,
            errors: grouping.containers_stop_with_err,
            no_errors: grouping.containers_stop - grouping.containers_stop_with_err
        };

        let firstGauge = ((newData['running'] / (newData['not_running'] + newData['running'])) || 0) * 100;
        let secondGauge = ((newData['paused'] / (newData['stopped'] + newData['paused'])) || 0) * 100;
        let thirdGauge = ((newData['no_errors'] / (newData['errors'] + newData['no_errors'])) || 0) * 100;
        if (firstGauge > 100) firstGauge = 100;
        if (secondGauge > 100) secondGauge = 100;
        if (thirdGauge > 100) thirdGauge = 100;

        let series = [{
            name: 'Running / Not Running',
            data: [{
                color: genericColors[0],
                radius: '112%',
                innerRadius: '88%',

                y: firstGauge
            }]
        }, {
            name: 'Paused / Stopped',
            data: [{
                color: genericColors[1],
                radius: '87%',
                innerRadius: '63%',

                y: secondGauge
            }]
        }, {
            name: 'Stopped w/o Errors / w/ Errors',
            data: [{
                color: genericColors[2],
                radius: '62%',
                innerRadius: '38%',

                y: thirdGauge
            }]
        }];

        setChartConfig(series);
    };

    const barChartBuilder = (data) => {
        var buildErrors = [];
        var composeErrors = [];
        var runErrors = [];

        data.forEach(datum => {
            if (datum.build_err) {
                buildErrors.push(datum)
            };
            if (datum.compose_err) {
                composeErrors.push(datum)
            };
            if (datum.run_err) {
                runErrors.push(datum)
            };
        });

        var allCats = [];

        var pods = data.map(datum => {
            return datum.pod_name
        });
        allCats = pods.filter((classType, index) => {
            return pods.indexOf(classType) === index;
        });
        
        setAllFilCats(allCats);

        // Build errors
        let buildErrNewData = [];
        for (let i = 0; i < allCats.length; i++) {
            let total = 0;
            for (let y = 0; y < buildErrors.length; y++) {
                if (allCats[i] === buildErrors[y].pod_name) {
                    total = total + buildErrors[y].build_err;
                };
            };
            buildErrNewData.push(parseFloat((total).toFixed(2)));
        };

        // Compose errors
        let composeErrNewData = [];
        for (let i = 0; i < allCats.length; i++) {
            let total = 0;
            for (let y = 0; y < composeErrors.length; y++) {
                if (allCats[i] === composeErrors[y].pod_name) {
                    total = total + composeErrors[y].compose_err;
                };
            };

            composeErrNewData.push(parseFloat((total).toFixed(2)));
        };

        // Run errors
        let runErrNewData = [];
        for (let i = 0; i < allCats.length; i++) {
            let total = 0;
            for (let y = 0; y < runErrors.length; y++) {
                if (allCats[i] === runErrors[y].pod_name) {
                    total = total + runErrors[y].run_err;
                };
            };
            runErrNewData.push(parseFloat((total).toFixed(2)));
        };

        var finalArray = [buildErrNewData, composeErrNewData, runErrNewData];

        var chartInfo = [];
        for (let i = 0; i < sortingArr.length; i++) {
            chartInfo.push({
                name: sortingArr[i],
                data: finalArray[i]
            });
        };
        setGraphData(chartInfo);

    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Node Status</VsiH4>
        <Row>
            <Col lg={4}>
                <VsiBubble autobotName='ActivityGauge' autobotInfo={{ data: chartConfig }}>
                    <Container style={{height: '550px'}}>
                        <Row className={"py-5 d-flex justify-content-center"}>
                            <Col xs={12} sm={12} md={2} style={{width: '260px'}}>
                                <ActivityGauge title={'Containers Status'} series={chartConfig}/>
                            </Col>
                        </Row>
                        <Row className="d-flex justify-content-center">
                            <Col xs={12} sm={8} md={6}>
                                <table style={{padding: 0, margin: '0 auto'}}>
                                    <tr>
                                        <td width={25} height={10} style={{backgroundColor: genericColors[0], padding: 0}}>
                                        </td>
                                        <td width={25}
                                            style={{backgroundColor: Highcharts.color(genericColors[0]).setOpacity(0.3).get()}}></td>
                                        <td style={{
                                            color: basicTextStyle.color,
                                            fontSize: 10,
                                            marginLeft: 10,
                                            paddingLeft: 10,
                                        }}>Running / Not Running
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width={25} height={10} style={{backgroundColor: genericColors[1], padding: 0}}>
                                        </td>
                                        <td width={25}
                                            style={{backgroundColor: Highcharts.color(genericColors[1]).setOpacity(0.3).get()}}></td>
                                        <td style={{
                                            color: basicTextStyle.color,
                                            fontSize: 10,
                                            marginLeft: 10,
                                            paddingLeft: 10
                                        }}>Paused / Stopped
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width={25} height={10} style={{backgroundColor: genericColors[2], padding: 0}}>
                                        </td>
                                        <td width={25}
                                            style={{backgroundColor: Highcharts.color(genericColors[2]).setOpacity(0.3).get()}}></td>
                                        <td style={{
                                            color: basicTextStyle.color,
                                            fontSize: 10,
                                            marginLeft: 10,
                                            paddingLeft: 10
                                        }}>Stopped w/o Errors / w/ Errors
                                        </td>
                                    </tr>
                                </table>
                            </Col>
                        </Row>
                    </Container>
                </VsiBubble>
            </Col>
            <Col lg={8}>
                <VsiBubble>
                    <VerticalBarChart
                        title="POD Health"
                        categories={allFilCats}
                        height={550}
                        includeLegend={true}
                        series={graphData}
                    />
                </VsiBubble>
            </Col>
        </Row>
        <Row>
            <Col>
                <VsiBubble>
                    <Grid
                        data={data?.recordset || []}
                        columnDefs={colDefs}
                    />
                </VsiBubble>
            </Col>
        </Row>
    </Card>
}
